import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import first from 'lodash-es/first';
import noop from 'lodash-es/noop';
import { BehaviorSubject, Observable, Subscription, throwError as observableThrowError } from 'rxjs';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';

import { PatientAppointmentInventorySearchGraphQL } from '@app/appointment/patient-appointment-inventory-search-graphql.service';

import { ApiService } from '../core/api.service';
import { FeatureFlags } from '../core/feature-flags/feature-flags';
import { LaunchDarklyService } from '../core/feature-flags/launchdarkly.service';
import { AppointmentSearchState } from './appointment-search-service/appointment-search-state';
import { AppointmentType, BlankAppointmentType } from './appointment-type';
import { AppointmentInventory, DayInventories, ProviderInventories } from './provider-inventories';

export interface RecommendedRemoteResults {
  remoteResults: ProviderInventories[];
  remoteAppointmentType: AppointmentType;
}

interface SearchResponse {
  response: any;
  isGraphQL: boolean;
}

@Injectable()
export class AppointmentSearchService {
  private _loading$ = new BehaviorSubject<boolean>(true);
  readonly loading$ = this._loading$.asObservable();

  private _remoteResults$ = new BehaviorSubject<RecommendedRemoteResults>(null);
  readonly remoteResults$: Observable<RecommendedRemoteResults> = this._remoteResults$.asObservable();

  private _results$ = new BehaviorSubject<ProviderInventories[]>(null);
  readonly results$: Observable<ProviderInventories[]> = this._results$.asObservable();

  private _meta$ = new BehaviorSubject<AppointmentSearchMetaData>(null);
  readonly meta$: Observable<AppointmentSearchMetaData> = this._meta$.asObservable();

  private _errors$ = new BehaviorSubject<any>(null);
  readonly errors$: Observable<any> = this._errors$.asObservable();

  private requestSubscription: Subscription;
  searchEndpoint = '/api/v2/patient/appointment_search';

  constructor(
    private apiService: ApiService,
    private launchDarklyService: LaunchDarklyService,
    private appointmentInventorySearchGraphQL: PatientAppointmentInventorySearchGraphQL,
  ) {}

  createNewSearchRequest(searchState: AppointmentSearchState): Observable<SearchResponse> {
    return this.launchDarklyService
      .featureFlag$(FeatureFlags.GRAPHQL_MIGRATION_APPOINTMENT_INVENTORY_SEARCH, false)
      .pipe(
        take(1),
        switchMap(useGraphQL =>
          useGraphQL
            ? this.createGraphQLRequest(searchState).pipe(map(response => ({ response, isGraphQL: true })))
            : this.createRestRequest(searchState).pipe(map(response => ({ response, isGraphQL: false }))),
        ),
      );
  }

  getResults(searchState: AppointmentSearchState): Observable<object> {
    this._loading$.next(true);
    this.unsubscribeFromLastOutstandingRequest();
    const request = this.createNewSearchRequest(searchState).pipe(
      map(({ response, isGraphQL }) => this.mapInventoryResponse(response, searchState, isGraphQL)),
      tap((providerList: ProviderInventories[]) => {
        this._results$.next(providerList);
        this._loading$.next(false);
        this._errors$.next(null);
      }),
      catchError((error: HttpErrorResponse) => {
        this._errors$.next(error);
        this._loading$.next(false);
        return observableThrowError(error);
      }),
    );

    // noop in error handler so error is not rethrown without ability to catch
    this.requestSubscription = request.subscribe({ error: noop });
    return request;
  }

  mapInventoryResponse(respJson: any, searchState: AppointmentSearchState, isGraphQL: boolean): ProviderInventories[] {
    const providerList: ProviderInventories[] = (respJson.results ?? []).map((result: any) =>
      isGraphQL ? AppointmentInventory.fromAppointmentSearchGraphQL(result) : ProviderInventories.fromApiV2(result),
    );

    const remoteRecommendations: ProviderInventories[] = (
      respJson.recommended_remote_results ??
      respJson.recommendedRemoteResults ??
      []
    ).map((result: any) =>
      isGraphQL ? AppointmentInventory.fromAppointmentSearchGraphQL(result) : ProviderInventories.fromApiV2(result),
    );

    const remoteAppointmentType =
      respJson.recommended_remote_appointment_type ?? respJson.recommendedRemoteAppointmentType
        ? isGraphQL
          ? AppointmentType.fromGraphQL(respJson.recommendedRemoteAppointmentType)
          : AppointmentType.fromApiV2(respJson.recommended_remote_appointment_type)
        : new BlankAppointmentType();

    this._remoteResults$.next({ remoteAppointmentType, remoteResults: remoteRecommendations });

    const inventory_ids = providerList.flatMap(provider =>
      Object.values(provider.dayInventories)
        .flat()
        .map(inventory => inventory.id),
    );

    const meta: AppointmentSearchMetaData = new AppointmentSearchMetaData(
      respJson,
      remoteRecommendations,
      inventory_ids,
      isGraphQL,
    );
    searchState.searchMetadata = meta;
    this._meta$.next(meta);

    return providerList;
  }

  getScrollableResults(
    providerId: number,
    start: string,
    end: string,
    serviceAreaId: number,
    appointmentTypeId: number,
  ): Observable<ProviderInventories[]> {
    const searchParams = {
      service_area_id: serviceAreaId,
      appointment_type_id: appointmentTypeId,
      onsite_only: false,
      date_start: start,
      date_end: end,
      recommend_remote_results: false,
      provider_id: providerId,
    };

    return this.launchDarklyService
      .featureFlag$(FeatureFlags.GRAPHQL_MIGRATION_APPOINTMENT_INVENTORY_SEARCH, false)
      .pipe(
        take(1),
        switchMap(useGraphQL =>
          useGraphQL ? this.getScrollableResultsGraphQL(searchParams) : this.getScrollableResultsRest(searchParams),
        ),
      );
  }

  mapScrollableInventoryResponse(respJson: any, isGraphQL: boolean): ProviderInventories[] {
    return (respJson.results ?? []).map((result: any) =>
      isGraphQL ? AppointmentInventory.fromAppointmentSearchGraphQL(result) : ProviderInventories.fromApiV2(result),
    );
  }

  private createRestRequest(searchState: AppointmentSearchState): Observable<any> {
    return this.apiService.post(this.searchEndpoint, searchState.searchParams, false);
  }

  private createGraphQLRequest(searchState: AppointmentSearchState): Observable<any> {
    return this.appointmentInventorySearchGraphQL
      .fetch({ input: this.mapSearchParamsToGraphQL(searchState.searchParams) }, { fetchPolicy: 'network-only' })
      .pipe(map(response => response.data.patientAppointmentInventorySearch));
  }

  private getScrollableResultsRest(searchParams: any): Observable<ProviderInventories[]> {
    return this.apiService.post(this.searchEndpoint, searchParams).pipe(
      map(response => this.mapScrollableInventoryResponse(response, false)),
      map(providerInventories => providerInventories.filter(provider => provider.hasInventories)),
    );
  }

  private getScrollableResultsGraphQL(searchParams: any): Observable<ProviderInventories[]> {
    return this.appointmentInventorySearchGraphQL
      .fetch({ input: this.mapSearchParamsToGraphQL(searchParams) }, { fetchPolicy: 'network-only' })
      .pipe(
        map(response => response.data.patientAppointmentInventorySearch),
        map(response => this.mapScrollableInventoryResponse(response, true)),
        map(providerInventories => providerInventories.filter(provider => provider.hasInventories)),
      );
  }

  private unsubscribeFromLastOutstandingRequest(): void {
    if (this.requestSubscription) {
      this.requestSubscription.unsubscribe();
    }
  }

  private mapSearchParamsToGraphQL(params: any): any {
    return {
      appointmentTypeId: params.appointment_type_id?.toString(),
      dateStart: params.date_start,
      dateEnd: params.date_end,
      serviceAreaId: params.service_area_id?.toString(),
      officeIds: params.office_ids?.map((id: number) => id.toString()),
      providerId: params.provider_id?.toString(),
      onsiteOnly: params.onsite_only,
      recommendRemoteResults: params.recommend_remote_results,
      providerLimit: params.provider_limit,
      providerOffset: params.provider_offset,
      groupByDate: params.group_by_date,
      topicName: params.topic_name,
      stateCode: params.state_code,
      providerIds: params.provider_ids?.map((id: number) => id.toString()),
    };
  }
}

export class AppointmentSearchMetaData {
  providerCount: number;
  inventoryCount: number;
  inventoryIds: number[];
  recommendedRemoteResultsProviderCount: number;
  recommendedRemoteResultsInventoryCount: number;
  recommendedRemoteResultsInventoryIds: number[];
  remoteAppointmentTypeId: number;

  constructor(
    respJson: Record<string, any>,
    remoteRecommendations: ProviderInventories[],
    inventoryIds: number[],
    isGraphQL: boolean,
  ) {
    const hasRemoteRecommendations =
      remoteRecommendations &&
      remoteRecommendations.length > 0 &&
      remoteRecommendations[0]?.dayInventories &&
      Object.keys(remoteRecommendations[0].dayInventories).length > 0;

    this.providerCount = isGraphQL ? respJson?.providerCount : respJson?.provider_count ?? 0;
    this.inventoryCount = isGraphQL ? respJson?.inventoryCount : respJson?.inventory_count ?? 0;
    this.inventoryIds = inventoryIds;
    this.remoteAppointmentTypeId = isGraphQL
      ? respJson?.recommendedRemoteAppointmentType?.id
      : respJson?.recommended_remote_appointment_type?.id;
    this.recommendedRemoteResultsProviderCount = hasRemoteRecommendations ? 1 : 0;
    this.recommendedRemoteResultsInventoryIds = hasRemoteRecommendations
      ? this.getRemoteInventory(remoteRecommendations[0].dayInventories)
      : [];
    this.recommendedRemoteResultsInventoryCount = this.recommendedRemoteResultsInventoryIds.length;
  }

  private getRemoteInventory(inventories: DayInventories): number[] {
    const maxVisibleRemoteInventories = 5;
    const firstDateAvailable = first(Object.keys(inventories));
    const remoteInventories = inventories[firstDateAvailable]?.slice(0, maxVisibleRemoteInventories);

    return remoteInventories ? remoteInventories.map(slot => slot.id) : [];
  }
}
